import React from 'react';
import PropTypes from 'prop-types';
import Card from '../../atoms/Card/Card';
import ArticleHeader from '../../molecules/ArticleHeader/ArticleHeader';
import ArticleImage from '../../atoms/ArticleImage/ArticleImage';
import ArticleActions from '../../molecules/ArticleActions/ArticleActions';
import { useTheme } from '@mui/material/styles';
import { Checkbox, Box } from '@mui/material';
import {
  AttachMoney as ValuationIcon,
  People as AudienceIcon,
  ThumbUp as LikesIcon,
  Straighten as MeasurementIcon
} from '@mui/icons-material';
import TagSummary from '../../molecules/TagSummary';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import TagChip from '../../atoms/TagChip/TagChip';
import AddTagButton from '../../molecules/AddTagButton/AddTagButton';
import { useViewSettings } from '../../../context/ViewSettingsContext';
import {
  operationalComponentSurfaces,
  getOperationalText,
  operationalElevation,
  operationalRadii,
  operationalText,
  operationalTypography
} from '../../../design-system/tokens';
import { formatDuration, getBrandDurationSeconds, isTimeBasedMedia } from '../../../utils/editorialOccupancy';

/**
 * ArticleCardBase - Organismo base para todos os cards de artigo
 * Centraliza renderização de imagem, header, conteúdo, tags e actions.
 */

// Função para formatar números grandes (K, M, B) - formato compacto
const formatCompactNumber = (num) => {
  if (!num || num === 0) return '0';

  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Função para formatar valores monetários compactos
const formatCompactCurrency = (value) => {
  const numericValue = Number(value);
  if (!numericValue || Number.isNaN(numericValue)) return 'R$ 0';
  const formatAmount = (amount, maximumFractionDigits = 0, minimumFractionDigits = 0) => (
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount)
  );

  if (numericValue >= 1000000) {
    return 'R$ ' + formatAmount(numericValue / 1000000, 1) + 'M';
  }
  if (numericValue >= 1000) {
    return 'R$ ' + formatAmount(numericValue / 1000, 1) + 'K';
  }
  return 'R$ ' + formatAmount(numericValue, 2, Number.isInteger(numericValue) ? 0 : 2);
};

const formatCompactCurrencyAmount = (value) => (
  formatCompactCurrency(value).replace(/^R\$\s*/, '')
);

const ArticleCardBase = ({
  article,
  variant = 'default',
  onArticleClick,
  onTagClick,
  onTagRemove,
  onToggleFavorite,
  onToggleRead,
  onUpdateSentiment,
  onEdit,
  onEditTags,
  onAddTag,
  selectionMode = false,
  isSelected = false,
  onSelect,
  className = '',
  actions,
  imageOverlay,
  infoOverlay,
  infoFooter,
  footerActions,
  onSendNewsletter,
  height,
  ...props
}) => {
  const theme = useTheme();
  const { fontSize } = useViewSettings();
  const isDetailed = variant === 'detailed';
  const isDarkMode = theme.palette.mode === 'dark';
  const detailedContentInset = 16;
  const detailedImageChromeHeight = 0;
  const textTone = getOperationalText(isDarkMode);
  const cardType = operationalTypography.articleCard;
  const dateToneColor = textTone.date;
  const quietMetricColor = textTone.quiet;
  const metricShelfTextColor = textTone.metric;
  const metricShelfBackground = isDarkMode ? 'rgba(255, 255, 255, 0.025)' : 'rgba(15, 23, 42, 0.035)';
  const tagContrastTextColor = theme.palette.text.primary;
  const neutralTagChipColorSx = {
    backgroundColor: quietMetricColor,
    borderColor: quietMetricColor,
    color: tagContrastTextColor,
    opacity: 0.88,
    '& .MuiChip-label': {
      color: tagContrastTextColor
    },
    '& .MuiChip-deleteIcon': {
      color: `${tagContrastTextColor} !important`
    },
    '& .MuiChip-icon': {
      color: tagContrastTextColor
    },
    '&:hover': {
      backgroundColor: quietMetricColor,
      borderColor: quietMetricColor,
      opacity: 0.96
    }
  };
  const detailedTagChipSx = {
    height: cardType.tag.height,
    minHeight: cardType.tag.height,
    px: 0.75,
    borderRadius: '999px',
    ...neutralTagChipColorSx,
    border: 0,
    fontSize: cardType.tag.fontSize,
    fontWeight: cardType.tag.fontWeight,
    backdropFilter: 'none',
    '&:hover': {
      ...neutralTagChipColorSx['&:hover'],
      transform: 'none'
    },
    '& .MuiChip-label': {
      px: 0,
      ...neutralTagChipColorSx['& .MuiChip-label'],
      fontSize: cardType.tag.fontSize,
      fontWeight: cardType.tag.fontWeight
    },
    '& .MuiChip-deleteIcon': {
      ml: 0.4,
      mr: -0.35,
      fontSize: cardType.tag.deleteIconSize,
      ...neutralTagChipColorSx['& .MuiChip-deleteIcon']
    }
  };
  const detailedMetricPillStyle = {
    minHeight: 20,
    padding: 0,
    borderRadius: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    color: metricShelfTextColor,
    backgroundColor: 'transparent',
    fontSize: cardType.metric.fontSize,
    fontWeight: cardType.metric.fontWeight,
    lineHeight: 1,
    opacity: 0.94,
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 2
  };
  const detailedMetricPrimaryStyle = {
    ...detailedMetricPillStyle,
    color: metricShelfTextColor,
    backgroundColor: 'transparent',
    fontWeight: cardType.metric.primaryWeight,
    opacity: 0.94
  };
  const renderHiddenTagsTooltip = (hiddenTags, chipSx) => (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        maxWidth: 260,
        padding: '2px 0'
      }}
    >
      {hiddenTags.map((tag) => {
        const tagId = typeof tag === 'object' ? tag._id : tag;
        const label = typeof tag === 'object' ? (tag.name || tag.label || tag._id) : tag;

        return (
          <TagChip
            key={tagId}
            label={label}
            size="small"
            fontSize={customFontSize}
            sx={chipSx}
            onDelete={onTagRemove ? () => onTagRemove(tagId) : undefined}
            onClick={onTagClick ? () => onTagClick(tag) : undefined}
          />
        );
      })}
    </div>
  );

  // Extrair sx externo para aplicar DEPOIS das propriedades críticas
  const { sx: externalSx, ...cardProps } = props;

  // Calcular fontSize 90% da escolha do usuário para as tags
  let customFontSize = '0.875rem'; // Valor padrão caso o usuário não tenha escolhido
  if (fontSize) {
    const match = fontSize.match(/([\d.]+)(rem|px|em)?/);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2] || 'rem';
      customFontSize = (value * 0.90) + unit;
    }
  }

  // Handler para clique no checkbox
  const handleCheckboxClick = (e) => {
    e.stopPropagation(); // Impedir que propague para o card
    if (onSelect) {
      onSelect(article._id);
    }
  };

  // Handler customizado que considera o modo seleção
  const handleCardClick = (e) => {
    // Ctrl+clique sempre seleciona (atalho rápido)
    if (e.ctrlKey || e.metaKey) {
      e.stopPropagation();
      // Remover foco de inputs para permitir atalhos de teclado
      if (document.activeElement && document.activeElement.tagName === 'INPUT') {
        document.activeElement.blur();
      }
      if (onSelect) {
        onSelect(article._id);
      }
      return;
    }

    // Clique normal sempre abre o modal (mesmo em modo seleção)
    if (onArticleClick) {
      onArticleClick(article, e);
    }
  };

  return (
    <Card
      variant={variant}
      elevation={0}
      onClick={handleCardClick}
      selected={isSelected}
      className={`article-card article-card--${variant} ${className}`}
      sx={{
        fontSize: 'inherit',
        ...(externalSx || {}), // Aplicar sx externo ANTES das propriedades críticas
        // Propriedades críticas com !important para não serem sobrescritas
        ...(isDetailed && {
          minHeight: { xs: 360, sm: 390, md: 430 },
          overflow: 'hidden !important',
          display: 'flex !important',
          flexDirection: 'column !important',
          paddingTop: 0,
          borderRadius: `${operationalRadii.surface}px`,
          border: '0 !important',
          background: operationalComponentSurfaces.stage,
          boxShadow: operationalElevation.surface,
          '&:hover': {
            borderColor: 'transparent'
          },
          '& .article-title--large': {
            color: `${textTone.mutedStrong} !important`,
            display: '-webkit-box !important',
            WebkitBoxOrient: 'vertical !important',
            WebkitLineClamp: '2 !important',
            whiteSpace: 'normal !important',
            fontSize: `${cardType.title.fontSize} !important`,
            lineHeight: `${cardType.title.lineHeight} !important`,
            minHeight: `${cardType.title.minHeight} !important`,
            fontWeight: `${cardType.title.fontWeight} !important`,
            marginBottom: `${cardType.title.marginBottom} !important`
          },
          '& .article-info': {
            color: `${dateToneColor} !important`,
            fontSize: `${cardType.source.fontSize} !important`,
            fontWeight: `${cardType.source.fontWeight} !important`
          }
        }),
        ...(height && { height: height })
      }}
      {...cardProps}
    >
      {/* Imagem */}
      <Box sx={isDetailed ? {
        position: 'relative',
        flexShrink: 0,
        width: '100%',
        height: { xs: 170 + detailedImageChromeHeight, sm: 190 + detailedImageChromeHeight, md: 220 + detailedImageChromeHeight },
        alignSelf: 'center',
        marginTop: 0,
        marginBottom: 0,
        borderTopLeftRadius: operationalRadii.surface,
        borderTopRightRadius: operationalRadii.surface,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        overflow: 'hidden',
        backgroundColor: operationalComponentSurfaces.stage,
        borderBottom: 0,
        boxShadow: 'none'
      } : { position: 'relative', flexShrink: 0 }}>
        {isDetailed && (
          <Box
            sx={{
              position: 'absolute',
              top: detailedImageChromeHeight,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              backgroundColor: isDarkMode
                ? operationalComponentSurfaces.media.dark
                : operationalComponentSurfaces.media.light
            }}
          >
            <ArticleImage
              src={article}
              alt={article.title}
              variant={variant}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}
        {!isDetailed && (
          <ArticleImage src={article} alt={article.title} variant={variant} />
        )}

        {/* Checkbox de seleção (aparece apenas em modo seleção) */}
        {selectionMode && (
          <Checkbox
            checked={isSelected}
            onClick={handleCheckboxClick}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 10,
              bgcolor: operationalComponentSurfaces.mediaControl.background,
              borderRadius: `${operationalRadii.control}px`,
              padding: '4px', // Compactar um pouco o padding para compensar o ícone maior
              '&:hover': {
                bgcolor: operationalComponentSurfaces.mediaControl.backgroundHover,
              },
              '& .MuiSvgIcon-root': {
                color: isSelected ? 'primary.main' : operationalText.dark.primary,
                fontSize: '1.4rem', // Ícone bem visível
              },
            }}
          />
        )}

        {imageOverlay && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
            {typeof imageOverlay === 'function' ? imageOverlay() : imageOverlay}
          </div>
        )}
        {infoOverlay && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            {typeof infoOverlay === 'function' ? infoOverlay() : infoOverlay}
          </Box>
        )}
        {infoFooter && (
          <Box sx={{ width: '100%' }}>
            {typeof infoFooter === 'function' ? infoFooter() : infoFooter}
          </Box>
        )}
      </Box>

      {/* Conteúdo principal (header + tags) */}
      {!infoOverlay && (
        <div style={isDetailed ? {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          flex: 1,
          justifyContent: 'flex-start',
          position: 'relative',
          zIndex: 1,
          width: '100%',
          alignSelf: 'center',
          backgroundColor: operationalComponentSurfaces.stage,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: operationalRadii.surface,
          borderBottomRightRadius: operationalRadii.surface,
          boxShadow: 'none',
          marginBottom: 0,
          paddingBottom: 8
        } : { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div style={isDetailed ? {
            paddingLeft: detailedContentInset,
            paddingRight: 22,
            paddingTop: 22,
            paddingBottom: 0
          } : { padding: 12, paddingBottom: 0 }}>
            <ArticleHeader article={article} variant={variant} onTitleClick={selectionMode ? undefined : () => onArticleClick && onArticleClick(article)} />
          </div>
          <div style={{ padding: variant === 'detailed' ? `0 22px 0 ${detailedContentInset}px` : 12, paddingTop: 0, paddingBottom: 0 }}>
            {variant === 'detailed' ? (
              // Removido ArticleContent para ocultar descrição que contém informações de visualizações/inscritos
              null
            ) : null}
          </div>
          {variant === 'detailed' ? (
            <div style={{
              paddingLeft: detailedContentInset,
              paddingRight: 0,
              paddingBottom: 0,
              paddingTop: 0,
              marginTop: 'auto',
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              minHeight: 38
            }}>
              {article.tags && article.tags.length > 0 && (
                <TagSummary tags={article.tags} maxWidth="100%" fontSize={customFontSize} chipSx={detailedTagChipSx} onTagRemove={onTagRemove} onTagClick={onTagClick} maxVisible={10} maxRows={2}>
                  {({ hiddenTags }) => {
                    return (
                      <>
                        {hiddenTags.length > 0 && (
                          <div style={{ position: 'relative', width: 'auto', padding: 0, margin: 0 }}>
                            <Tooltip title={renderHiddenTagsTooltip(hiddenTags, detailedTagChipSx)} placement="top" arrow disableInteractive={false}>
                              <span>
                                <TagChip label={`+${hiddenTags.length}`} size="small" fontSize={customFontSize} sx={{ ...detailedTagChipSx, m: 0, mr: 0, ml: 0, minWidth: 0, px: '9px', '& .MuiChip-label': { ...detailedTagChipSx['& .MuiChip-label'], px: 0 } }} />
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </>
                    );
                  }}
                </TagSummary>
              )}
            </div>
          ) : variant !== 'minimal' && (
            <div style={{
              paddingLeft: 12,
              paddingRight: 0,
              paddingBottom: 0,
              paddingTop: 0,
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              minHeight: 46
            }}>
              {article.tags && article.tags.length > 0 && (
                <TagSummary tags={article.tags} maxWidth="100%" fontSize={customFontSize} chipSx={neutralTagChipColorSx} onTagRemove={onTagRemove} onTagClick={onTagClick} maxVisible={10} maxRows={2}>
                  {({ hiddenTags }) => {
                    return (
                      <>
                        {hiddenTags.length > 0 && (
                          <div style={{ position: 'relative', width: 'auto', padding: 0, margin: 0 }}>
                            <Tooltip title={renderHiddenTagsTooltip(hiddenTags, neutralTagChipColorSx)} placement="top" arrow disableInteractive={false}>
                              <span>
                                <TagChip label={`+${hiddenTags.length}`} size="small" fontSize={customFontSize} sx={{ ...neutralTagChipColorSx, m: 0, mr: 0, ml: 0, minWidth: 0, px: '4px', borderRadius: `${operationalRadii.pill}px`, '& .MuiChip-label': { ...neutralTagChipColorSx['& .MuiChip-label'], px: '4px' } }} />
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </>
                    );
                  }}
                </TagSummary>
              )}
            </div>
          )}
          {actions && actions.length > 0 && (
            <div style={{ padding: variant === 'detailed' ? 16 : 12, paddingTop: 0, paddingBottom: 0 }}>
              <ArticleActions actions={actions} variant={variant} />
            </div>
          )}
        </div>
      )}

      {/* Footer com métricas em linha - SEMPRE MOSTRAR EM DETAILED */}
      {variant === 'detailed' && (() => {
        const isOnlineOrImpresso = ['Online', 'Impresso', 'Jornal', 'Revista'].includes(article.mediaType) ||
          ['Online', 'Impresso', 'Jornal', 'Revista'].includes(article.source);
        const timeBased = isTimeBasedMedia(article);
        const occupancyCm2 = article.editorialOccupancy?.valueCm2 || article.editorialOccupancy?.editorialAreaBrandRelevantCm2 || 0;
        const occupancyDuration = getBrandDurationSeconds(article);
        const showOccupancy = article.editorialOccupancy?.enabled && ((timeBased && occupancyDuration > 0) || (isOnlineOrImpresso && occupancyCm2 > 0));
        const hasMetrics = (article.valuation > 0) || (article.likesNumber > 0) || (article.viewsNumber > 0) || showOccupancy;

        return (
          <div style={{
            paddingLeft: detailedContentInset,
            paddingRight: (footerActions || onAddTag) ? 44 : 14,
            paddingTop: 4,
            paddingBottom: 4,
            minHeight: 36,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            background: metricShelfBackground,
            color: metricShelfTextColor,
            boxShadow: 'none',
            borderTop: 0,
            borderBottomLeftRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
            marginBottom: 0
          }}>
            {/* Mensagem quando não há métricas */}
            {!hasMetrics && (
              <span style={{
                fontSize: cardType.metric.fontSize,
                color: metricShelfTextColor,
                fontStyle: 'italic',
                fontWeight: cardType.metric.fontWeight,
                opacity: 0.62,
                position: 'relative',
                zIndex: 1
              }}>
                Métricas não disponíveis
              </span>
            )}

            {/* Valoração */}
            {article.valuation > 0 && (
              <div style={detailedMetricPrimaryStyle} title="Valoração">
                <ValuationIcon sx={{ fontSize: cardType.metric.iconSize, color: 'inherit' }} />
                <span translate="no" style={{
                  color: 'inherit',
                  fontWeight: 'inherit'
                }}>
                  {formatCompactCurrencyAmount(article.valuation)}
                </span>
              </div>
            )}

            {/* Curtidas */}
            {article.likesNumber > 0 && (
              <div style={detailedMetricPillStyle} title="Curtidas">
                <LikesIcon sx={{ fontSize: cardType.metric.iconSize, color: 'inherit' }} />
                <span translate="no" style={{
                  color: 'inherit',
                  fontWeight: 'inherit'
                }}>
                  {formatCompactNumber(article.likesNumber)}
                </span>
              </div>
            )}

            {/* Visualizações / Audiência Diária */}
            {(() => {
              const isOnline = article.mediaType === 'Online' || article.source === 'Online';
              
              // Para Online: tentar pegar audiência de várias fontes
              if (isOnline) {
                let dailyAudience = null;
                
                // 1. Prioridade: audience já calculado (audiência diária)
                if (article.audience && article.audience > 0) {
                  dailyAudience = article.audience;
                }
                // 2. Fallback: calcular a partir da audiência mensal
                else {
                  const monthlyAudience = article.domainInfo?.estimatedAudience
                    || article.domainInfo?.audienceMonthly
                    || article.estimatedAudience
                    || article.audienceMonthly;
                  
                  if (monthlyAudience && monthlyAudience > 0) {
                    dailyAudience = Math.round(monthlyAudience / (365 / 12));
                  }
                }
                
                // Se encontrou audiência, mostrar
                if (dailyAudience && dailyAudience > 0) {
                  return (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      ...detailedMetricPillStyle
                    }} title="Audiência Diária Estimada">
                      <AudienceIcon sx={{ fontSize: cardType.metric.iconSize, color: 'inherit' }} />
                      <span translate="no" style={{
                        color: 'inherit',
                        fontWeight: 'inherit'
                      }}>
                        {formatCompactNumber(dailyAudience)}
                      </span>
                    </div>
                  );
                }
              }

              // Para outros tipos de mídia (não Online), mostra viewsNumber (Impacto)
              if (!isOnline && article.viewsNumber > 0) {
                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    ...detailedMetricPillStyle
                  }} title="Visualizações / Impacto">
                    <AudienceIcon sx={{ fontSize: cardType.metric.iconSize, color: 'inherit' }} />
                    <span translate="no" style={{
                      color: 'inherit',
                      fontWeight: 'inherit'
                    }}>
                      {formatCompactNumber(article.viewsNumber)}
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            {showOccupancy && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                ...detailedMetricPillStyle
              }} title={`Ocupação editorial estimada | Participação da marca: ${Math.round((article.editorialOccupancy.brandParticipationPercent || 0) * 100)}%`}>
                <MeasurementIcon sx={{ fontSize: cardType.metric.iconSize, color: 'inherit' }} />
                <span translate="no" style={{
                  color: 'inherit',
                  fontWeight: 'inherit'
                }}>
                  {timeBased ? formatDuration(occupancyDuration) : `${Number(occupancyCm2).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} cm²`}
                </span>
              </div>
            )}

            {(footerActions || onAddTag) && (
              <div style={{
                position: 'absolute',
                right: 8,
                bottom: 6,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: metricShelfTextColor,
                gap: '4px',
                zIndex: 2
              }}>
                {onAddTag && (
                  <AddTagButton
                    tooltip="Adicionar tag"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddTag(article);
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      mt: 0,
                      backgroundColor: 'transparent',
                      opacity: 0.86,
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)',
                        opacity: 1
                      },
                      '& .MuiSvgIcon-root': {
                        color: `${metricShelfTextColor} !important`,
                        opacity: 0.76
                      }
                    }}
                  />
                )}
                {footerActions && (
                  typeof footerActions === 'function' ? footerActions() : footerActions
                )}
              </div>
            )}
          </div>
        );
      })()}
    </Card>
  );
};

ArticleCardBase.propTypes = {
  article: PropTypes.object.isRequired,
  variant: PropTypes.string,
  onArticleClick: PropTypes.func,
  onTagClick: PropTypes.func,
  onTagRemove: PropTypes.func,
  onToggleFavorite: PropTypes.func,
  onToggleRead: PropTypes.func,
  onUpdateSentiment: PropTypes.func,
  onEdit: PropTypes.func,
  onEditTags: PropTypes.func,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  className: PropTypes.string,
  actions: PropTypes.array,
  imageOverlay: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  infoOverlay: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  infoFooter: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  footerActions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  renderTags: PropTypes.func,
  onSendNewsletter: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

// Memoizar para evitar re-renders desnecessários
export default React.memo(ArticleCardBase);
